import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, readU16, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16 } from "./limits.ts";

/**
 * The `looks` frame (ADR-094 decision 6, PROTOCOL.md 5.16): server to client, the looks of the characters entering a
 * page's view or changing look, each as its handle and a wire look of 13 bytes. It is sent in exactly the ticks a
 * character's Name component is written for that client and when a look changes, before that tick's snapshot, never
 * per tick. A map server writes it into a buffer its session allocates at join (`putLookEntry`, `putLooksHeader`),
 * so a send allocates nothing for any character; the decoder fills a record it reuses and never throws (ADR-030).
 */

/**
 * Bytes of a wire look: `u16` skin, `u16` hair, `u8` flags, `u16` outfit, `u16` hat, `u16` main hand, `u16` off hand.
 */
export const LOOK_BYTES = 13;

/**
 * Bytes of one character in the frame: its `u16` handle and its wire look.
 */
export const LOOK_ENTRY_BYTES = 2 + LOOK_BYTES;

/**
 * Bytes before the first character: the frame header and the `u16` count.
 */
export const LOOKS_HEADER_BYTES = HEADER_BYTES + 2;

/**
 * The most characters a frame carries: its count is a `u16`. A map server sends at most the characters of one view.
 */
export const MAX_LOOKS = 65_535;

/**
 * The bits of a wire look's flags byte this build knows: none. The byte is written 0, and a reader keeps only the
 * bits it knows, so a later bit never breaks an older page.
 */
export const KNOWN_LOOK_FLAGS = 0;

const MAX_BYTE = 255;
const SKIN_AT = 2;
const HAIR_AT = 4;
const FLAGS_AT = 6;
const OUTFIT_AT = 7;
const HAT_AT = 9;
const MAIN_HAND_AT = 11;
const OFF_HAND_AT = 13;

/**
 * A wire look: a piece's number in each slot, its place in the rules' looks table counting from 1, 0 for nothing, and
 * the flags byte as it travelled.
 */
export interface WireLook {
  skin: number;
  hair: number;
  flags: number;
  outfit: number;
  hat: number;
  mainHand: number;
  offHand: number;
}

/**
 * One character of a `looks` frame: its handle and its wire look.
 */
export interface LookEntry extends WireLook {
  handle: number;
}

/**
 * A decoded `looks` frame, reused across frames: how many characters it held and the characters, the first `count`
 * of `entries` valid.
 */
export interface LooksRecord {
  count: number;
  readonly entries: LookEntry[];
}

/**
 * A wire look of nothing in every slot, for a caller to fill and reuse.
 *
 * @returns The look.
 */
export function newWireLook(): WireLook {
  return { skin: 0, hair: 0, flags: 0, outfit: 0, hat: 0, mainHand: 0, offHand: 0 };
}

/**
 * A fresh record for `decodeLooks` to fill.
 *
 * @param capacity Entries to make now, so no frame of up to that many characters allocates; a view's 512 on a page.
 * @returns An empty record.
 */
export function newLooksRecord(capacity = 0): LooksRecord {
  const entries: LookEntry[] = [];
  for (let index = 0; index < capacity; index += 1) {
    entries.push({ handle: 0, skin: 0, hair: 0, flags: 0, outfit: 0, hat: 0, mainHand: 0, offHand: 0 });
  }
  return { count: 0, entries };
}

/**
 * Bytes of a frame of a number of characters.
 *
 * @param count The characters.
 * @returns `5 + 15 * count`.
 */
export function looksBytes(count: number): number {
  return LOOKS_HEADER_BYTES + LOOK_ENTRY_BYTES * count;
}

/**
 * Writes one character at its place in a frame through plain byte stores, for the tick path: it makes nothing and
 * checks nothing. The caller has sized the buffer (`looksBytes` of the most characters it will write, a view's 512 on
 * a map server), keeps the index under that count itself, and holds every field to its width: a store past the end of
 * a typed array is dropped silently, so a short buffer loses bytes without an error. `encodeLooks` is the checked
 * form, for tests and tools; it words an error per field and is not for a tick.
 *
 * @param buffer The frame.
 * @param index The character's place, from 0.
 * @param handle Its handle.
 * @param look Its wire look.
 */
export function putLookEntry(buffer: Uint8Array, index: number, handle: number, look: Readonly<WireLook>): void {
  const at = LOOKS_HEADER_BYTES + LOOK_ENTRY_BYTES * index;
  putU16(buffer, at, handle);
  putU16(buffer, at + SKIN_AT, look.skin);
  putU16(buffer, at + HAIR_AT, look.hair);
  buffer[at + FLAGS_AT] = look.flags;
  putU16(buffer, at + OUTFIT_AT, look.outfit);
  putU16(buffer, at + HAT_AT, look.hat);
  putU16(buffer, at + MAIN_HAND_AT, look.mainHand);
  putU16(buffer, at + OFF_HAND_AT, look.offHand);
}

/**
 * Writes a frame's header once its characters are in place.
 *
 * @param buffer The frame.
 * @param count The characters written, 0 to 65,535.
 * @returns The frame's length in bytes.
 */
export function putLooksHeader(buffer: Uint8Array, count: number): number {
  putHeader(buffer, MessageType.Looks);
  putU16(buffer, HEADER_BYTES, count);
  return looksBytes(count);
}

/**
 * Encodes a `looks` frame into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `looksBytes(count)` long.
 * @param entries The characters, each a handle and a wire look.
 * @param count How many of them to write, from the first; all of them unless given.
 * @returns Bytes written.
 * @throws Error when the count is not a whole number from 0 to the entries held and 65,535, the buffer is too small,
 *   or a field does not fit its width.
 */
export function encodeLooks(buffer: Uint8Array, entries: readonly Readonly<LookEntry>[], count = entries.length): number {
  if (!Number.isInteger(count) || count < 0 || count > entries.length || count > MAX_LOOKS) {
    throw new Error(`looks count ${count} is not a whole number from 0 to ${Math.min(entries.length, MAX_LOOKS)}`);
  }
  const length = looksBytes(count);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte looks frame`);
  }
  for (let index = 0; index < count; index += 1) {
    const entry = entries[index] as Readonly<LookEntry>;
    assertField(entry.handle, MAX_U16, `looks handle ${entry.handle}`);
    assertField(entry.skin, MAX_U16, `looks skin ${entry.skin}`);
    assertField(entry.hair, MAX_U16, `looks hair ${entry.hair}`);
    assertField(entry.flags, MAX_BYTE, `looks flags ${entry.flags}`);
    assertField(entry.outfit, MAX_U16, `looks outfit ${entry.outfit}`);
    assertField(entry.hat, MAX_U16, `looks hat ${entry.hat}`);
    assertField(entry.mainHand, MAX_U16, `looks mainHand ${entry.mainHand}`);
    assertField(entry.offHand, MAX_U16, `looks offHand ${entry.offHand}`);
    putLookEntry(buffer, index, entry.handle, entry);
  }
  return putLooksHeader(buffer, count);
}

/**
 * Decodes a `looks` frame into a record. Never throws: a frame shorter than its header and count (truncated), a
 * length other than `5 + 15 * count` or another type is malformed, and another version a mismatch, leaving the record
 * untouched. Every value of every field is well formed: the flags byte is handed on as it travelled, unknown bits
 * and all, and a piece number the reader's table lacks is the reader's to settle (`settleWireLook`). The record's
 * entries are reused, and one is made only when a frame holds more characters than any before it.
 *
 * @param frame The whole frame.
 * @param out Record to fill.
 * @returns The verdict.
 */
export function decodeLooks(frame: Uint8Array, out: LooksRecord): DecodeVerdict {
  if (frame.length < LOOKS_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = readU16(frame, HEADER_BYTES);
  const header = checkHeader(frame, MessageType.Looks, looksBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  const entries = out.entries;
  for (let index = 0; index < count; index += 1) {
    const at = LOOKS_HEADER_BYTES + LOOK_ENTRY_BYTES * index;
    let entry = entries[index];
    if (entry === undefined) {
      entry = { handle: 0, skin: 0, hair: 0, flags: 0, outfit: 0, hat: 0, mainHand: 0, offHand: 0 };
      entries.push(entry);
    }
    entry.handle = readU16(frame, at);
    entry.skin = readU16(frame, at + SKIN_AT);
    entry.hair = readU16(frame, at + HAIR_AT);
    entry.flags = frame[at + FLAGS_AT] as number;
    entry.outfit = readU16(frame, at + OUTFIT_AT);
    entry.hat = readU16(frame, at + HAT_AT);
    entry.mainHand = readU16(frame, at + MAIN_HAND_AT);
    entry.offHand = readU16(frame, at + OFF_HAND_AT);
  }
  out.count = count;
  return OK;
}
